import { useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import type { GeneratedImage } from '../../../../types/generatedImage';
import { AssetPreviewModal } from '../AssetPreviewModal';

interface GeneratedImagePreviewModalProps {
  image: GeneratedImage | null;
  open: boolean;
  onClose: () => void;
}

const providerLabels: Record<string, string> = {
  openAi: 'OpenAI',
  gemini: 'Gemini',
  openSources: 'Replicate',
  stabilityAi: 'Stability AI',
  ideogram: 'Ideogram',
  fal: 'FAL',
};

function GeneratedImagePreviewModal({
  image,
  open,
  onClose,
}: GeneratedImagePreviewModalProps) {
  const [isImageLoading, setIsImageLoading] = useState(true);

  if (!open || !image) return null;

  return (
    <AssetPreviewModal
      open
      onClose={onClose}
      name={image.name}
      provider={image.provider}
      providerLabels={providerLabels}
      model={image.model}
      createdAt={image.createdAt}
      assetUrl={image.imageUrl}
      downloadFileName={image.name || 'generated-image'}
      fullscreenEnabled
      maxWidth='md'
      contentMinHeight='200px'
      renderContent={({ isFullscreen, isDarkMode }) => (
        <>
          {isImageLoading && (
            <CircularProgress size={32} sx={{ position: 'absolute' }} />
          )}
          <Box
            component='img'
            src={image.imageUrl}
            alt='Generated image preview'
            onLoad={() => setIsImageLoading(false)}
            sx={{
              maxWidth: '100%',
              maxHeight: isFullscreen ? 'calc(100vh - 160px)' : '70vh',
              objectFit: 'contain',
              borderRadius: '8px',
              opacity: isImageLoading ? 0 : 1,
              transition: 'opacity 0.3s ease',
              boxShadow: isDarkMode
                ? '0px 1px 5px 0px rgba(0, 0, 0, 0.3), 0px 2px 2px 0px rgba(0, 0, 0, 0.2)'
                : '0px 1px 5px 0px rgba(0, 0, 0, 0.08), 0px 2px 2px 0px rgba(0, 0, 0, 0.06)',
            }}
          />
        </>
      )}
    />
  );
}

export { GeneratedImagePreviewModal };
